/**
 * LectureSlideDeckChangesDiff
 *
 * Displays a diff of changes for a single lecture slide deck, including deck-level and file-level attributes.
 * Deck-level attributes include metadata and associations; file-level attributes include file upload details.
 */
import { type FC, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { type RootState } from '@editor2/reducers/root';
import { type LectureSlideDeckPayload } from '@editor2/types/unsavedChangesPayload';
import ChangesDiff from '../common/ChangesDiff';
import { getInitialIndex, buildFamilyIdLabels } from '../helpers';
import { chapterHeader } from '../styles';

interface Props {
	courseId: number;
	deckChanges: LectureSlideDeckPayload;
	index: number;
}

const LectureSlideDeckChangesDiff: FC<Props> = ({ courseId, deckChanges, index }) => {
	const { lecture_slide_decks, initialCourse, chapters, pages, book } = useSelector(
		(state: RootState) => state.courses[courseId]
	);

	// Remove id and file from deckChanges to get only deck-level attributes
	const { id, file: _file, ...deckLevelAttributes } = deckChanges;
	const isNew = !!deckChanges.family_id;

	const currentDeck = isNew
		? lecture_slide_decks.entities[deckChanges.family_id!]
		: Object.values(lecture_slide_decks.entities).find((d) => d.id === id);

	const initialDeck = id
		? Object.values(initialCourse.lecture_slide_decks?.entities ?? {}).find((d) => d?.id === id)
		: undefined;

	const deckFamilyId = currentDeck?.family_id ?? initialDeck?.family_id;

	const initialIds = initialCourse.lecture_slide_decks?.ids ?? [];
	const initialIndex = isNew ? -1 : getInitialIndex(initialIds, deckFamilyId ?? '');

	// Build a family_id → label map for human-readable associated content display.
	const familyIdLabels = useMemo(
		() => buildFamilyIdLabels({ chapters, pages, yamlConfig: book.yaml_config }),
		[chapters, pages, book.yaml_config]
	);

	// Deck-level attributes
	const currentDeckLevelAttributes = buildCurrentDeckLevelAttributes(
		deckLevelAttributes,
		familyIdLabels
	);
	const initialDeckLevelAttributes = buildInitialDeckLevelAttributes(initialDeck, familyIdLabels);

	// File-level attributes
	const fileAttributeKeys = [
		'original_name',
		'filesize_bytes',
		'uploaded_at',
		'uploader_name',
		'notes'
	] as const;
	const currentFile = currentDeck?.file;
	const initialFile = initialDeck?.file;
	const fileLevelAttributes = buildFileLevelAttributes(
		isNew,
		currentFile,
		initialFile,
		fileAttributeKeys
	);

	const attributeChanges = { ...currentDeckLevelAttributes, ...fileLevelAttributes.current };
	const initialAttributes = { ...initialDeckLevelAttributes, ...fileLevelAttributes.initial };

	// If only an id was sent (no-op marker) and position hasn't changed, skip rendering.
	const isPositionChanged = initialIndex >= 0 && index !== initialIndex;
	if (!isNew && !isPositionChanged && Object.keys(attributeChanges).length === 0) return null;

	const fileName =
		currentDeck?.file?.original_name ?? initialDeck?.file?.original_name ?? 'Lecture Slide Deck';
	const title = `File: ${fileName}`;

	return (
		<ChangesDiff
			title={title}
			index={index}
			initialIndex={initialIndex}
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			initial={initialAttributes as any}
			attributeChanges={attributeChanges}
			styles={chapterHeader}
			isNewElement={isNew}
		/>
	);
};

/**
 * Format a list of family IDs as "Label (family_id)" strings for display.
 */
function formatAssociatedIds(ids: string[], familyIdLabels: Map<string, string>): string {
	return ids
		.map((fid) => {
			const label = familyIdLabels.get(fid);
			return label ? `${label} (${fid})` : fid;
		})
		.join(', ');
}

/**
 * Build deck-level diffable attributes, including associated content labels.
 */
function buildCurrentDeckLevelAttributes(
	deckLevelAttributes: Record<string, unknown>,
	familyIdLabels: Map<string, string>
): Record<string, unknown> {
	const attrs: Record<string, unknown> = {};
	for (const [key, value] of Object.entries(deckLevelAttributes)) {
		if (key === 'associated_family_ids' && Array.isArray(value)) {
			attrs['associated_content'] = formatAssociatedIds(value, familyIdLabels);
		} else {
			attrs[key] = value;
		}
	}
	return attrs;
}

/**
 * Build initial deck-level attributes for diffing.
 */
function buildInitialDeckLevelAttributes(
	initialDeck: any,
	familyIdLabels: Map<string, string>
): Record<string, unknown> {
	const attrs: Record<string, unknown> = {};
	if (initialDeck) {
		if (initialDeck.associated_family_ids) {
			attrs['associated_content'] = formatAssociatedIds(
				initialDeck.associated_family_ids,
				familyIdLabels
			);
		}
		if (initialDeck.self_serve_sample !== undefined) {
			attrs.self_serve_sample = initialDeck.self_serve_sample;
		}
	}
	return attrs;
}

/**
 * Build file-level diffable attributes, comparing current and initial file objects.
 */
function buildFileLevelAttributes(
	isNew: boolean,
	currentFile: any,
	initialFile: any,
	fileAttributeKeys: readonly string[]
): { current: Record<string, unknown>; initial: Record<string, unknown> } {
	const current: Record<string, unknown> = {};
	const initial: Record<string, unknown> = {};
	if (isNew && currentFile) {
		for (const attr of fileAttributeKeys) {
			const val = currentFile[attr];
			if (val !== undefined && val !== null && val !== '') {
				current[attr] = typeof val === 'number' ? String(val) : val;
			}
		}
	} else if (currentFile) {
		for (const attr of fileAttributeKeys) {
			const currVal = currentFile[attr];
			const initVal = initialFile?.[attr];
			if (currVal !== initVal) {
				current[attr] = typeof currVal === 'number' ? String(currVal) : currVal ?? '';
				if (initVal !== undefined && initVal !== null) {
					initial[attr] = typeof initVal === 'number' ? String(initVal) : initVal;
				}
			}
		}
	}
	return { current, initial };
}

export default LectureSlideDeckChangesDiff;
